<template>
  <div
    class="cal-agenda"
    v-bind="passThrough('agenda')"
  >
    <div class="cal-agenda__header">
      <div>
        <div class="cal-agenda__weekday">{{ date.format('dddd') }}</div>
        <h3 class="cal-agenda__title">{{ date.format('D MMMM YYYY') }}</h3>
      </div>
      <button
        type="button"
        class="cal-agenda__close"
        :aria-label="$t('ui.actions.close')"
        @click="emit('close')"
      >
        <Icon type="xmark" />
      </button>
    </div>

    <div
      v-if="events.length === 0"
      class="cal-agenda__empty"
    >
      <slot name="empty">
        {{ $t('ui.messages.noResults') }}
      </slot>
    </div>

    <ul
      v-else
      class="cal-agenda__list"
    >
      <li
        v-for="event in events"
        :key="event.id"
      >
        <button
          type="button"
          class="cal-agenda__item"
          @click="emit('event-click', event, $event)"
          @dblclick="emit('event-dblclick', event, $event)"
        >
          <span
            class="cal-agenda__accent"
            :style="{ backgroundColor: resolveCalendarColor(event.color) }"
          />
          <span class="cal-agenda__body">
            <span class="cal-agenda__item-title">{{ event.title }}</span>
            <span class="cal-agenda__item-time">
              {{ event.allDay ? $t('ui.common.dateTime.allDay') : formatEventTime(event, { hour12, timezone, showEnd: true }) }}
            </span>
            <span
              v-if="event.description"
              class="cal-agenda__item-description"
            >
              {{ event.description }}
            </span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs'

// components
import Icon from '@/components/icon/Icon.vue'

// types
import type { CalendarEvent } from '@/types/components/calendar'

// utilities
import { resolveCalendarColor } from '@/utilities/calendar/color'
import { formatEventTime } from '@/utilities/calendar/format'
import { useCalendarPassThrough } from '@/utilities/calendar/passthrough'

withDefaults(
  defineProps<{
    /** Day being listed */
    date: Dayjs
    /** Events on that day, already sorted */
    events: CalendarEvent[]
    /** Use 12-hour clock labels */
    hour12?: boolean
    /** Calendar-level fallback timezone */
    timezone?: string
  }>(),
  {
    hour12: false,
    timezone: undefined
  }
)

const emit = defineEmits<{
  close: []
  'event-click': [event: CalendarEvent, payload: MouseEvent]
  'event-dblclick': [event: CalendarEvent, payload: MouseEvent]
}>()

const passThrough = useCalendarPassThrough()
</script>

<style scoped>
.cal-agenda {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background-color: var(--bg-primary);
}

.cal-agenda__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-3);
  padding: var(--s-3) var(--s-4);
  border-bottom: 1px solid var(--border-secondary);
}

.cal-agenda__weekday {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.cal-agenda__title {
  margin: 0.125rem 0 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-primary);
}

.cal-agenda__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: var(--text-secondary);
  background: transparent;
  border-radius: var(--rounded-md);
}

.cal-agenda__close:hover {
  background-color: var(--bg-primary_hover);
}

.cal-agenda__empty {
  padding: var(--s-6) var(--s-4);
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  text-align: center;
}

.cal-agenda__list {
  margin: 0;
  padding: var(--s-2) 0;
  list-style: none;
  overflow: auto;
}

.cal-agenda__item {
  display: flex;
  gap: var(--s-3);
  width: 100%;
  padding: var(--s-3) var(--s-4);
  text-align: left;
  background: transparent;
}

.cal-agenda__item:hover {
  background-color: var(--bg-primary_hover);
}

.cal-agenda__accent {
  flex-shrink: 0;
  width: 0.25rem;
  align-self: stretch;
  border-radius: var(--rounded-full);
}

.cal-agenda__body {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.cal-agenda__item-title {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
}

.cal-agenda__item-time {
  font-size: var(--text-xs);
  color: var(--text-tertiary);
}

.cal-agenda__item-description {
  margin-top: 0.25rem;
  font-size: var(--text-xs);
  line-height: 1.45;
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
